/**
 * Overlay tenant-specific sandbox IDs, then rewrite scenario JSON that still
 * uses the aos_test canonical IDs (employee 4, position 92, …).
 */
import type { QaTimecardConfig, QaTimecardDefaults } from './types';

export function mergeTenantDefaults(
  config: QaTimecardConfig,
  tenantCode?: string | null
): QaTimecardDefaults {
  const code = String(tenantCode || '').trim().toLowerCase();
  const overlay = (config.tenantDefaults && config.tenantDefaults[code]) || {};
  return { ...config.defaults, ...overlay };
}

function buildIdMaps(fromDefaults: QaTimecardDefaults, toDefaults: QaTimecardDefaults) {
  const locationId: Record<number, number> = {};
  if (fromDefaults.locationId != null && toDefaults.locationId != null) {
    locationId[fromDefaults.locationId] = toDefaults.locationId;
  }
  if (fromDefaults.locationId2 != null && toDefaults.locationId2 != null) {
    locationId[fromDefaults.locationId2] = toDefaults.locationId2;
  }
  const scheduleId: Record<number, number> = {};
  if (fromDefaults.scheduleId != null && toDefaults.scheduleId != null) {
    scheduleId[fromDefaults.scheduleId] = toDefaults.scheduleId;
  }
  if (fromDefaults.scheduleId2 != null && toDefaults.scheduleId2 != null) {
    scheduleId[fromDefaults.scheduleId2] = toDefaults.scheduleId2;
  }
  return {
    employeeId:
      fromDefaults.employeeId != null && toDefaults.employeeId != null
        ? { [fromDefaults.employeeId]: toDefaults.employeeId }
        : ({} as Record<number, number>),
    locationId,
    excludeLocationId: locationId,
    positionId:
      fromDefaults.positionId != null && toDefaults.positionId != null
        ? { [fromDefaults.positionId]: toDefaults.positionId }
        : ({} as Record<number, number>),
    scheduleId,
  };
}

const LOCATION_ARRAY_KEYS = new Set(['ensureSubLocations', 'syncLocations']);

export function remapSandboxIds<T>(
  value: T,
  fromDefaults: QaTimecardDefaults,
  toDefaults: QaTimecardDefaults
): T {
  const maps = buildIdMaps(fromDefaults, toDefaults) as Record<string, Record<number, number>>;

  function walk(node: unknown, parentKey: string | null): unknown {
    if (Array.isArray(node)) {
      if (parentKey && LOCATION_ARRAY_KEYS.has(parentKey)) {
        return node.map((id) =>
          typeof id === 'number' && maps.locationId[id] != null ? maps.locationId[id] : walk(id, parentKey)
        );
      }
      return node.map((item) => walk(item, parentKey));
    }
    if (node && typeof node === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
        out[k] = walk(v, k);
      }
      return out;
    }
    if (typeof node === 'number' && parentKey && maps[parentKey] && maps[parentKey][node] != null) {
      return maps[parentKey][node];
    }
    return node;
  }

  return walk(value, null) as T;
}
